import {
	Table,
	Icons,
	Stack,
	Text,
	Box,
	// Dropdown,
	joinAttributes,
	RelativeTime,
	Inline,
	AsyncSelect,
	AsyncSelectProps,
	MoneySum,
} from "@sembark-travel/ui-base"
import {
	CursorListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useEffect } from "react"
// import config from "../config"
import { PhoneNumber, Email } from "../Contacts"
import { generatePath } from "../router-utils"
import { TPreference, TTouristProfile } from "./store"
import { ILocation, SelectLocations } from "../Locations"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import { DateRangePickerField, SelectField } from "@sembark-travel/ui-form"
// import { TemporaryStorage } from "../DownloadManager"
import { ISimpleListResponse, useXHR } from "@sembark-travel/xhr"

type TFilters = TSearchParams & {
	created_after?: Date
	created_before?: Date
	locations?: Array<ILocation>
}

type TFiltersInQuery = TSearchParams & {
	ca?: string
	cb?: string
	locations?: Array<string>
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const { q, page, created_after, created_before, locations } = filters
	const query: TFiltersInQuery = {}
	if (page) {
		query.page = page
	}
	if (q) {
		query.q = q
	}
	if (created_after) {
		query.ca = dateToQuery(created_after)
	}
	if (created_before) {
		query.cb = dateToQuery(created_before)
	}
	if (locations?.length) {
		query.locations = locations.map((l) => `${l.id}__${l.full_name}`)
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const { q, page, ca, cb, locations } = query
	const filters: TFilters = {}
	if (page) {
		filters.page = page
	}
	if (q) {
		filters.q = q
	}
	if (ca) {
		filters.created_after = parseDateFromQuery(ca)
	}
	if (cb) {
		filters.created_before = parseDateFromQuery(cb)
	}
	if (locations?.length) {
		filters.locations = locations.map((l) => {
			const [id, ...name] = l.split("__")
			return {
				id,
				full_name: name.join("__"),
			} as never as ILocation
		})
	}
	return filters
}

function filtersToRequest({
	created_after,
	created_before,
	locations,
	...params
}: TFilters) {
	return {
		...params,
		created_after: created_after
			? dateToUTCString(startOf(created_after, "day"))
			: null,
		created_before: created_before
			? dateToUTCString(endOf(created_before, "day"))
			: null,
		locations: locations?.length ? locations.map((l) => l.id) : [],
	}
}

export function TouristProfilesList() {
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Search
			initialParams={params}
			title={"Tourist Profiles"}
			onSearch={(params) => setParams({ ...params, cursor: null })}
			Filters={Filters}
			actions={
				<Inline gap="4">
					<ButtonLink
						to={generatePath("/tourist-profiles/new")}
						level="primary"
						status="primary"
						anchored
					>
						<Icons.Plus title="Add" /> New Tourist Profile
					</ButtonLink>
					{/* <Dropdown alignRight="sm">
						<Dropdown.ToggleButton>
							<Icons.DotsVertical />
						</Dropdown.ToggleButton>
						<Dropdown.Menu>
							<TemporaryStorage
								initUrl="/tourist-profiles/download"
								initParams={{
									...filtersToRequest(params),
									timezone_offset: config.timezoneOffset,
								}}
							>
								{({ message, generate, url, reset }) =>
									url ? (
										<Dropdown.MenuItem
											as="a"
											href={url}
											target="_blank"
											download
											onClick={() => reset()}
										>
											{message}
										</Dropdown.MenuItem>
									) : (
										<Dropdown.MenuItem
										onClick={(e: React.MouseEvent) => {
											e.preventDefault()
											generate()
										}}
										>
											{message || (
												<>
													<Icons.DocumentDownload /> Download as CSV/Excel
												</>
											)}
										</Dropdown.MenuItem>
									)
								}
							</TemporaryStorage>
						</Dropdown.Menu>
					</Dropdown> */}
				</Inline>
			}
		>
			<CursorListView<TTouristProfile, TFilters>
				pageKey="tourists-profiles"
				params={params}
				fetch={(xhr, params) =>
					xhr
						.get(`/tourist-profiles`, {
							params: {
								...filtersToRequest(params),
							},
						})
						.then((resp) => resp.data)
				}
				onCursorChange={(cursor) => setParams({ ...params, cursor })}
			>
				{({ items: touristProfiles }) => (
					<Table
						striped
						bordered
						responsive
						headers={[
							"Name",
							"Contact",
							"Destinations",
							"Preferences",
							"Total Revenue",
						]}
						rows={touristProfiles?.map((touristProfile) => {
							const {
								id,
								name,
								phone_numbers,
								email,
								address,
								created_at,
								destinations,
								meal_preference,
								stay_preference,
								language_preference,
								total_revenue,
							} = touristProfile
							return [
								<Link
									color="accent"
									fontWeight="semibold"
									to={generatePath("/tourist-profiles/:touristProfileId", {
										touristProfileId: id.toString(),
									})}
								>
									<Stack>
										<Text>{name}</Text>
										<Text fontSize="sm">
											{joinAttributes(
												address ? address.location.name : "",
												<RelativeTime timestamp={created_at} />
											)}
										</Text>
									</Stack>
								</Link>,
								<Stack gap="px" style={{ minWidth: "180px" }}>
									<Box>
										<PhoneNumber value={phone_numbers} />
									</Box>
									{email ? (
										<Box>
											<Email value={email} />
										</Box>
									) : null}
								</Stack>,
								<Box>
									{destinations?.map((d) => d.name).join(", ") || null}
								</Box>,
								<Box>
									<Box>
										<Inline>
											Meal :{" "}
											{meal_preference ?? (
												<Text color={"muted"}>&nbsp;N/A</Text>
											)}
										</Inline>
									</Box>
									<Box>
										<Inline>
											Stay :{" "}
											{stay_preference ? (
												stay_preference
											) : (
												<Text color="muted">&nbsp;N/A</Text>
											)}
										</Inline>
									</Box>
									<Box>
										<Inline>
											Language :{" "}
											{language_preference ?? (
												<Text color={"muted"}>&nbsp;N/A</Text>
											)}
										</Inline>
									</Box>
								</Box>,
								<Stack gap="px">
									{total_revenue ? (
										<MoneySum money={total_revenue} roundOff />
									) : (
										<Text color="muted">N/A</Text>
									)}
								</Stack>,
							]
						})}
					/>
				)}
			</CursorListView>
		</Search>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<DateRangePickerField
				fromName="created_after"
				toName="created_before"
				label="Created Between"
				dateFormat="DD MMM, YYYY"
			/>
			<SelectField
				select={SelectLocations}
				name="locations"
				multiple
				label="Cities/Locations"
				creatable={false}
				addressableType="tourists"
			/>
		</Stack>
	)
}

export function SelectTouristProfile({
	tripId,
	...props
}: Omit<AsyncSelectProps, "fetch"> & {
	tripId?: number | string
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			placeholder="Type to search..."
			searchable
			perFetchLimit={15}
			optionRenderer={({
				option: touristProfile,
			}: {
				option: TTouristProfile
			}) => {
				const { name, type } = touristProfile
				return (
					<Stack gap="2" color={"default"}>
						<Text>
							{type === "adult" ? <Icons.Adult /> : <Icons.Child />}
							{name}
						</Text>
					</Stack>
				)
			}}
			fetch={async (q, { page }) => {
				const resp = await xhr.get<ISimpleListResponse<TTouristProfile>>(
					"/tourist-profiles",
					{
						params: {
							trip: tripId,
							limit: 15,
							page,
							q,
						},
					}
				)
				return resp.data.data || []
			}}
		/>
	)
}

interface SelectPreferencesProps extends Omit<AsyncSelectProps, "fetch"> {
	type: "meal" | "stay" | "language"
}

export function SelectPreferences({ type, ...props }: SelectPreferencesProps) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			creatable
			fetch={async (q) => {
				const resp = await xhr.get<ISimpleListResponse<TPreference>>(
					`/tourist-profiles/preferences/${type}`,
					{
						params: {},
					}
				)
				return resp.data.data
			}}
		/>
	)
}
